import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import api from '../utils/Api';

const phone = value => value || '—';

export default function BulkContactModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    if (busy) return;
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setResult(null);
    setError('');
    onClose();
  };

  const chooseFile = event => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setResult(null);
    setError('');
    if (!/\.xlsx$/i.test(chosen.name)) { setError('Choose an Excel .xlsx file.'); return; }
    if (chosen.size > 5 * 1024 * 1024) { setError('The Excel file must be 5 MB or smaller.'); return; }
    setFile(chosen);
  };

  const review = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    setPreview(null);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post('/students/contacts/preview', form);
      setPreview(response.data);
      setSelected(new Set(response.data.ready.map(item => item.studentId)));
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to preview this file. Please try again.');
    } finally { setBusy(false); }
  };

  const save = async () => {
    const rows = preview?.ready.filter(item => selected.has(item.studentId)) || [];
    if (!rows.length) return;
    setBusy(true);
    setError('');
    try {
      const response = await api.post('/students/contacts/apply', { rows });
      setResult(response.data);
      if (response.data.saved) onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save the contacts. Please try again.');
    } finally { setBusy(false); }
  };

  const toggle = id => setSelected(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="lg">
      <DialogTitle>Upload Student Contacts</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Upload the completed contacts Excel file downloaded from this page. Empty phone cells keep existing numbers.
            Review the current and new numbers before saving. Student IDs must stay unchanged.
          </Alert>
          <Box>
            <Button component="label" variant="outlined" startIcon={<CloudUpload />} disabled={busy}>
              {file ? 'Choose another Excel file' : 'Choose Excel file'}
              <input hidden type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={chooseFile} />
            </Button>
            {file && <Typography component="span" sx={{ ml: 2, overflowWrap: 'anywhere' }}>{file.name}</Typography>}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {result ? (
            <>
              <Alert severity={result.issues.length ? 'warning' : 'success'}>
                Saved contacts for {result.saved} {result.saved === 1 ? 'student' : 'students'}.
                {result.issues.length ? ` ${result.issues.length} could not be saved.` : ''}
              </Alert>
              {result.issues.map((issue, index) => (
                <Typography key={`${issue.row}-${index}`} variant="body2">Row {issue.row}: {issue.message}</Typography>
              ))}
              {preview?.issues.length > 0 && (
                <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                  <Typography variant="body2" fontWeight="bold">Rows from the file that still need attention:</Typography>
                  {preview.issues.map((issue, index) => (
                    <Typography key={`${issue.row}-${index}`} variant="body2" color="error">
                      Row {issue.row}{issue.name ? ` (${issue.name})` : ''}: {issue.message}
                    </Typography>
                  ))}
                </Box>
              )}
              {result.issues.length > 0 && <Typography variant="body2">Preview the file again to review any remaining changes.</Typography>}
            </>
          ) : preview && (
            <>
              <Alert severity={preview.issues.length ? 'warning' : 'success'}>
                {preview.ready.length} {preview.ready.length === 1 ? 'student has' : 'students have'} changes;
                {' '}{preview.unchanged} unchanged; {preview.issues.length} {preview.issues.length === 1 ? 'row needs' : 'rows need'} attention.
              </Alert>
              {preview.issues.length > 0 && (
                <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                  {preview.issues.map((issue, index) => (
                    <Typography key={`${issue.row}-${index}`} variant="body2" color="error">
                      Row {issue.row}{issue.name ? ` (${issue.name})` : ''}: {issue.message}
                    </Typography>
                  ))}
                </Box>
              )}
              {preview.ready.length > 0 && (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small" stickyHeader aria-label="Contact changes to review">
                    <TableHead><TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected.size === preview.ready.length}
                          indeterminate={selected.size > 0 && selected.size < preview.ready.length}
                          onChange={event => setSelected(event.target.checked ? new Set(preview.ready.map(item => item.studentId)) : new Set())}
                          inputProps={{ 'aria-label': 'Select all students' }}
                        />
                      </TableCell>
                      <TableCell>Student</TableCell><TableCell>Current numbers</TableCell><TableCell>Numbers after saving</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{preview.ready.map(item => (
                      <TableRow key={item.studentId}>
                        <TableCell padding="checkbox"><Checkbox checked={selected.has(item.studentId)} onChange={() => toggle(item.studentId)} inputProps={{ 'aria-label': `Select ${item.name}` }} /></TableCell>
                        <TableCell><Typography variant="body2" fontWeight="bold">{item.name}</Typography><Typography variant="caption">{item.className} · Row {item.row}</Typography></TableCell>
                        <TableCell><Typography variant="body2">Primary: {phone(item.previous.guardianPhone)}</Typography><Typography variant="body2">Second: {phone(item.previous.guardianPhone2)}</Typography></TableCell>
                        <TableCell><Typography variant="body2">Primary: {phone(item.updates.guardianPhone || item.previous.guardianPhone)}</Typography><Typography variant="body2">Second: {phone(item.updates.guardianPhone2 || item.previous.guardianPhone2)}</Typography></TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>{result ? 'Close' : 'Cancel'}</Button>
        {result?.issues.length > 0 && <Button onClick={review} disabled={!file || busy} variant="outlined">Preview Again</Button>}
        {!result && <Button onClick={review} disabled={!file || busy} variant={preview ? 'outlined' : 'contained'}>Preview Changes</Button>}
        {preview && !result && <Button onClick={save} disabled={!selected.size || busy} variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}>Save {selected.size} {selected.size === 1 ? 'Student' : 'Students'}</Button>}
      </DialogActions>
    </Dialog>
  );
}
